import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

const ScrollContext = createContext();

export const useScrollContext = () => {
  const context = useContext(ScrollContext);
  if (!context) {
    throw new Error('useScrollContext must be used within a ScrollProvider');
  }
  return context;
};

export const ScrollProvider = ({ children }) => {
  const [visibleSections, setVisibleSections] = useState(new Set());

  const registerSection = useCallback((sectionName) => {
    return visibleSections.has(sectionName);
  }, [visibleSections]);

  const addVisibleSection = useCallback((sectionName) => {
    setVisibleSections(prev => new Set([...prev, sectionName]));
  }, []);

  const removeVisibleSection = useCallback((sectionName) => {
    setVisibleSections(prev => {
      const newSet = new Set(prev);
      newSet.delete(sectionName);
      return newSet;
    });
  }, []);

  const isSectionVisible = useCallback((sectionName) => {
    return visibleSections.has(sectionName);
  }, [visibleSections]);

  const getVisibleSections = useCallback(() => {
    return Array.from(visibleSections);
  }, [visibleSections]);

  const value = {
    registerSection,
    addVisibleSection,
    removeVisibleSection,
    isSectionVisible,
    getVisibleSections,
    visibleSections: Array.from(visibleSections),
  };

  return (
    <ScrollContext.Provider value={value}>
      {children}
    </ScrollContext.Provider>
  );
};

// Custom hook for section visibility tracking
export const useSectionVisibility = (sectionName, threshold = 0.3) => {
  const { addVisibleSection, removeVisibleSection } = useScrollContext();
  const [ref, inView] = useInView({
    threshold,
    rootMargin: '-20% 0px -20% 0px',
  });

  useEffect(() => {
    if (inView) {
      addVisibleSection(sectionName);
    } else {
      removeVisibleSection(sectionName);
    }
  }, [inView, sectionName, addVisibleSection, removeVisibleSection]);

  return { ref, inView };
};

export default ScrollContext;